// The night the home screen shows: tonight (/), or a past night from the
// archive (/archive/{date}), on its layers (the night's own levels and its
// week's). A change of night clears the rows so no card shows another
// night's statuses while the new one loads.
import { api, ApiError } from '../api/client';
import type { NightResponse, TodayLevel } from '../api/types';
import { createStore } from '../store';

/** Every level a night offers: its own, then its week's. */
export function allLevels(night: NightResponse): TodayLevel[] {
  return [...night.daily, ...night.weekly.levels];
}

export interface NightState {
  phase: 'idle' | 'loading' | 'ready' | 'error';
  /** The past night asked for, or null for tonight. */
  requested: string | null;
  night: NightResponse | null;
  error: string | null;
}

export const nightStore = createStore<NightState>({ phase: 'idle', requested: null, night: null, error: null });

let loadToken = 0;

/** Load tonight (null) or a past night; loading the same night again refreshes it in place. */
export async function loadNight(date: string | null): Promise<void> {
  const token = ++loadToken;
  const current = nightStore.get();
  const same = current.requested === date && current.night !== null;
  nightStore.set({ phase: same ? 'ready' : 'loading', requested: date, night: same ? current.night : null, error: null });
  try {
    const night = date ? await api.night(date) : await api.today();
    if (token !== loadToken) return;
    nightStore.set({ phase: 'ready', requested: date, night, error: null });
  } catch (err) {
    if (token !== loadToken) return;
    const message = err instanceof ApiError ? err.message : 'The sky is out of reach. Check your connection.';
    nightStore.set({ ...nightStore.get(), phase: 'error', error: message });
  }
}
